/**
 * ReturningCard — renders an INSERT/UPDATE/DELETE RETURNING expression list as
 * a card of pills.
 *
 * With `editContext` (INSERT mount only — decision 11) the card is live
 * (§2.4 S6b): each pill's `×` commits `remove-returning-item` (removing the
 * LAST item drops the whole clause, keyword included), and the footer becomes
 * an editReady-gated column picker (target-table columns minus expressions
 * already returned) committing `add-returning-item`. UPDATE / DELETE call
 * sites pass no editContext and render the § 1.4 disabled scaffolding
 * unchanged.
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addReturningItem, removeReturningItem } from '../../edit/edits/returningItems';
import { AddButton } from '../common/AddButton';
import { CommandPickerPopover } from '../common/CommandPickerPopover';
import { RemoveButton } from '../common/RemoveButton';
import { ClauseCard } from '../select/ClauseCard';
import { splitTopLevelCommas } from '../select/splitTopLevel';
import { vqeCopy } from '../../ux/vqeCopy';
import { recordEditFailure, safeExtractStructure } from './insertColumnMapHelpers';
import { computeReturningCandidates } from './insertTailHelpers';

export interface ReturningCardProps {
  readonly bufferText: string;
  readonly start: number;
  readonly end: number;
  readonly options: ParseOptions;
  /**
   * Test-id prefix; defaults to `vqe-insert-returning` so existing INSERT
   * call sites stay unchanged. UPDATE / DELETE pass their own prefix to
   * namespace the rendered ids.
   */
  readonly testIdPrefix?: string;
  /** Present only for plain-INSERT mounts (decision 11) — enables editing. */
  readonly editContext?: VqeEditContext;
}

function ReturningAddControl({
  editContext,
  testIdPrefix,
}: {
  readonly editContext: VqeEditContext;
  readonly testIdPrefix: string;
}) {
  const { items, textByKey, allReturned } = computeReturningCandidates(editContext);
  const enabled = items.length > 0;

  const onSelect = (key: string): void => {
    const text = textByKey.get(key);
    if (!text) return;
    addReturningItem(text, toCommitCtx(editContext), {
      structure: safeExtractStructure(editContext),
    }).catch(recordEditFailure);
  };

  return (
    <CommandPickerPopover
      triggerLabel="add"
      testId={`${testIdPrefix}-add`}
      disabled={!enabled}
      // Disabled-trigger why-tooltip: exhaustion (every column already
      // returned) is NOT "editing unavailable" — say the real reason. An
      // empty candidate list from a schema gap (target table missing from
      // the snapshot) keeps the noSnapshot copy instead.
      tooltip={
        allReturned ? vqeCopy.disabled.returningAllColumnsListed : vqeCopy.disabled.noSnapshot
      }
      items={items}
      onSelect={enabled ? onSelect : undefined}
      emptyMessage="Every column on this table is already returned."
    />
  );
}

export function ReturningCard({
  bufferText,
  start,
  end,
  options,
  testIdPrefix = 'vqe-insert-returning',
  editContext,
}: ReturningCardProps) {
  const items = splitTopLevelCommas(bufferText, start, end, options);
  const editEnabled = !!editContext;
  // The add-picker consumes the schema snapshot — full editReady gating
  // (decision 7); removal is free-text-convention (`!!editContext` alone).
  const editReady = editEnabled && !!editContext.service.getSchemaSnapshot();

  const footer =
    editContext && editReady ? (
      <ReturningAddControl editContext={editContext} testIdPrefix={testIdPrefix} />
    ) : (
      <AddButton
        label="add"
        testId={`${testIdPrefix}-add`}
        tooltip={editEnabled ? vqeCopy.disabled.noSnapshot : undefined}
      />
    );

  return (
    <ClauseCard title="Returning" testId={testIdPrefix} footer={footer}>
      {items.length === 0 ? (
        <p className="text-xs italic text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
          No expressions.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0" data-testid={`${testIdPrefix}-items`}>
          {items.map((item, idx) => (
            <li
              key={`${item.text}-${idx}`}
              className="flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-0.5"
              data-testid={`${testIdPrefix}-item-${idx}`}
            >
              <span className="font-mono text-xs" data-testid={`${testIdPrefix}-text-${idx}`}>
                {item.text}
              </span>
              <RemoveButton
                testId={`${testIdPrefix}-remove-${idx}`}
                ariaLabel="Remove expression"
                disabled={!editEnabled}
                tooltip={editEnabled ? 'Remove this expression' : undefined}
                onClick={
                  editContext
                    ? () => {
                        removeReturningItem(idx, toCommitCtx(editContext), {
                          structure: safeExtractStructure(editContext),
                        }).catch(recordEditFailure);
                      }
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
      )}
    </ClauseCard>
  );
}
